// API error logging utility for consistent error handling across services

/**
 * Log an API error with consistent formatting
 * @param {string} service - Service name where error occurred
 * @param {string} endpoint - API endpoint being called
 * @param {string} method - HTTP method (GET, POST, PUT, DELETE)
 * @param {Error} error - Error object
 * @param {Object} [context] - Additional context data (payload, params, etc.)
 */
export const logAPIError = (service, endpoint, method, error, context = {}) => {
  const errorInfo = {
    timestamp: new Date().toISOString(),
    service,
    endpoint,
    method,
    error: {
      message: error.message,
      name: error.name,
      status: error.response?.status,
      statusText: error.response?.statusText,
      data: error.response?.data,
      stack: process.env.NODE_ENV === 'development' ? error.stack : undefined
    },
    context: {
      ...context,
      url: error.config?.url,
      headers: error.config?.headers ? sanitizeHeaders(error.config.headers) : undefined
    }
  };

  // In development, show full error details
  if (process.env.NODE_ENV === 'development') {
    console.error('API Error Details:', errorInfo);
  } else {
    // In production, log minimal information
    console.error(`API Error - ${service}.${endpoint} [${method}]: ${error.message}`);
  }

  return errorInfo;
};

/**
 * Remove sensitive information from headers
 * @param {Object} headers - Request headers
 * @returns {Object} Sanitized headers
 */
const sanitizeHeaders = (headers) => {
  const sanitized = { ...headers };
  // Remove sensitive headers
  const sensitiveHeaders = ['authorization', 'cookie', 'x-api-key'];
  sensitiveHeaders.forEach(header => {
    if (sanitized[header]) {
      sanitized[header] = '[REDACTED]';
    }
  });
  return sanitized;
};

/**
 * Format API error message for user display
 * @param {Error} error - Error object
 * @param {string} defaultMessage - Default message if error doesn't have one
 * @returns {string} Formatted error message
 */
export const formatAPIError = (error, defaultMessage = 'An error occurred while connecting to the server') => {
  if (error.response?.data?.error) {
    return error.response.data.error;
  }

  if (error.response?.status) {
    switch (error.response.status) {
      case 400:
        return error.response.data?.message || 'Invalid request. Please check your input.';
      case 401:
        return 'Your session has expired. Please log in again.';
      case 403:
        return 'You do not have permission to perform this action.';
      case 404:
        return 'The requested resource was not found.';
      case 500:
        return 'An internal server error occurred. Please try again later.';
      default:
        return `Server error (${error.response.status}): ${error.message}`;
    }
  }

  if (error.request && !error.response) {
    return 'Unable to connect to the server. Please check your internet connection.';
  }

  return error.message || defaultMessage;
}; 